import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GameOptions from "./GameOptions";
import { useGameBot } from "./useGameBot";
import PlayerNameInput from "./PlayerNameInput";

export const NimGame = ({ onBack }) => {
  const navigate = useNavigate();
  // Game State
  const [heaps, setHeaps] = useState([3, 5, 7]);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  // Settings
  const [gameMode, setGameMode] = useState("PvP");
  const [difficulty, setDifficulty] = useState("Easy");
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");
  const [customHeaps, setCustomHeaps] = useState([3, 5, 7]);

  // Interaction State (for hovering)
  const [hoveredHeap, setHoveredHeap] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null); // Index within the heap

  const startGame = () => {
    setHeaps([...customHeaps]); // Use custom configuration
    setGameStarted(true);
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
  };

  const getPlayerName = (id) => {
    if (gameMode === "PvC" && id === 2) return "Computer";
    return id === 1 ? player1Name : player2Name;
  };

  // Move Logic
  const handleMove = useCallback(
    (heapIndex, amountToRemove) => {
      const newHeaps = [...heaps];
      newHeaps[heapIndex] -= amountToRemove;
      setHeaps(newHeaps);

      // Check if game over (sum of all heaps is 0)
      const remainingObjects = newHeaps.reduce((a, b) => a + b, 0);
      if (remainingObjects === 0) {
        setGameOver(true);
        // Misere play: The one who took the last object LOSES.
        // So the current player (who just moved) loses. The OTHER player wins.
        setWinner(currentPlayer === 1 ? 2 : 1);
      } else {
        setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
      }
    },
    [heaps, currentPlayer]
  );

  // Bot Logic
  const getBotMove = useCallback(
    (diff, currentGameState) => {
      const { heaps } = currentGameState;
      const validMoves = [];

      // Generate all valid moves: { heapIndex, amount }
      heaps.forEach((count, index) => {
        for (let i = 1; i <= count; i++) {
          validMoves.push({ heapIndex: index, amount: i });
        }
      });

      if (validMoves.length === 0) return null;

      if (diff === "Easy") {
        // Random move
        return validMoves[Math.floor(Math.random() * validMoves.length)];
      }

      // Hard / Medium - Use Nim Sum
      // Calculate Nim Sum
      const nimSum = heaps.reduce((acc, val) => acc ^ val, 0);

      // Find move to make Nim Sum 0 (or adapt for Misere)
      let optimalMove = null;

      // Check if text state will be "Endgame" (only heaps of size 1 remaining)
      // Actually, we check if the RESULT of the move leaves only heaps of size 0 or 1.
      // Misere Logic:
      // If we can leave the opponent with an ODD number of heaps of size 1, we win.
      // Otherwise, we play normally (Target Nim Sum 0).

      // Let's iterate all moves and score them.
      for (const move of validMoves) {
        const testHeaps = [...heaps];
        testHeaps[move.heapIndex] -= move.amount;
        
        // Check if this resulting state consists ONLY of heaps of size 0 or 1
        const isEndGame = testHeaps.every(h => h <= 1);
        
        if (isEndGame) {
           // We want the number of heaps with size 1 to be ODD.
           const onesCount = testHeaps.filter(h => h === 1).length;
           if (onesCount % 2 === 1) {
             return move; // Winning Misere move
           }
        } else {
           // Normal play: We want the new Nim Sum to be 0.
           const newNimSum = testHeaps.reduce((acc, val) => acc ^ val, 0);
           if (newNimSum === 0) {
             optimalMove = move;
           }
        }
      }

      if (optimalMove) return optimalMove;

      // If no winning move found (or existing position is losing), play random (or maybe try to prolong?)
      // Medium could sometimes skip optimal move
      if (diff === "Medium" && Math.random() < 0.3) {
          return validMoves[Math.floor(Math.random() * validMoves.length)];
      }

      return validMoves[Math.floor(Math.random() * validMoves.length)];
    },
    []
  );

  const { isBotThinking } = useGameBot({
     gameMode,
     makeMove: ({heapIndex, amount}) => handleMove(heapIndex, amount),
     playerTurn: currentPlayer,
     gameStarted,
     gameOver,
     botStrategy: getBotMove,
     gameState: { heaps }, // Pass heaps to bot
     difficulty
  });

  const handleObjectClick = (heapIndex, objectIndex) => {
    // Check validity
    if (!gameStarted || gameOver || isBotThinking) return;
    if (gameMode === "PvP" || currentPlayer === 1) {
       // Logic: Clicking the Nth object (0-indexed) means we take that object AND all objects after it?
       // Let's stick to the visual:
       // [ O O O X X ] <- If I hover X (index 3), I highlight index 3 and 4.
       // Taking them means I remove (heapSize - index) items.
       // Example: Heap has 5 items. Indices 0, 1, 2, 3, 4.
       // Click index 3. visual: 3 and 4 remove. Count = 2.
       // Items to remove = Total (5) - Index (3) = 2.
       
       const amountToRemove = heaps[heapIndex] - objectIndex;
       handleMove(heapIndex, amountToRemove);
    }
  };

  const isObjectHovered = (hIndex, oIndex) => {
     if (hoveredHeap !== hIndex) return false;
     // Highlight the hovered item and all items to the RIGHT (higher index)
     // Wait, usually people visualize removing from the END?
     // Let's assume objects vary from Left to Right.
     // If I remove 2 items, I remove from the Right.
     // So objects 0, 1 ... N. 
     // Hovering index K means I am removing index K, K+1... End.
     // YES.
     return oIndex >= hoveredIndex;
  };
  
  // Actually, let's refine the hover visual.
  // We should probably display the items, and if I hover the 3rd item, it implies I'm taking the 3rd, 4th, 5th... 
  // Wait, if I have 3 items: 1, 2, 3.
  // Taking 1 item -> Take 3.
  // Taking 2 items -> Take 2, 3.
  // Taking 3 items -> Take 1, 2, 3.
  // So hovering index i means removing items from i to (length-1).

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">
          Nim
        </h1>
        <p className="text-center text-gray-600 mb-8">
          The ancient game of strategy. Force your opponent to take the last object!
        </p>

        {!gameStarted ? (
           <div className="text-center">
            <GameOptions
              gameMode={gameMode}
              setGameMode={setGameMode}
              difficulty={difficulty}
              setDifficulty={setDifficulty}
              onStart={startGame}
            />
            
            <PlayerNameInput
              player1Name={player1Name}
              setPlayer1Name={setPlayer1Name}
              player2Name={player2Name}
              setPlayer2Name={setPlayer2Name}
              gameMode={gameMode}
            />

            {/* Custom Board Setup */}
            <div className="mb-6 max-w-lg mx-auto border-t pt-6">
              <label className="block text-lg font-medium text-gray-700 mb-4">
                Heap Configuration:
              </label>
              <div className="space-y-3">
                {customHeaps.map((heapSize, index) => (
                  <div key={index} className="flex items-center justify-center space-x-2">
                    <span className="text-gray-600 font-medium w-16">Heap {index + 1}:</span>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={heapSize}
                      onChange={(e) => {
                        const val = Math.max(1, Math.min(20, parseInt(e.target.value) || 1));
                        const newHeaps = [...customHeaps];
                        newHeaps[index] = val;
                        setCustomHeaps(newHeaps);
                      }}
                      className="w-20 px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 text-center"
                    />
                    <button
                      onClick={() => {
                        const newHeaps = customHeaps.filter((_, i) => i !== index);
                        setCustomHeaps(newHeaps);
                      }}
                      disabled={customHeaps.length <= 1}
                      className="p-2 text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Remove Heap"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                
                {customHeaps.length < 5 && (
                   <button
                     onClick={() => setCustomHeaps([...customHeaps, 3])}
                     className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center mx-auto"
                   >
                     + Add Heap
                   </button>
                )}
              </div>
            </div>

             <div className="mt-8 text-gray-600 max-w-2xl mx-auto">
              <h3 className="font-semibold mb-4 text-lg">Game Rules:</h3>
              <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2">
                <p>• There are multiple heaps of objects.</p>
                <p>• On your turn, choose ONE heap and take ANY number of objects from it (at least one).</p>
                <p>• Objects removed are gone from the game.</p>
                <p>• The player forced to take the very last object loses (Misere play).</p>
              </div>
            </div>
           </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="text-xl font-semibold">
                {gameOver ? (
                  <span className="text-green-600">
                    🎉 {getPlayerName(winner)} Wins!
                  </span>
                ) : (
                  <span>
                    Current Player:
                    <span
                      className={
                        currentPlayer === 1
                          ? "text-blue-600 ml-2"
                          : "text-red-600 ml-2"
                      }
                    >
                      {getPlayerName(currentPlayer)}
                    </span>
                     {isBotThinking && <span className="ml-2 text-sm text-gray-500">(Thinking...)</span>}
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                   setGameStarted(false);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                New Game
              </button>
            </div>

            <div className="flex flex-col items-center space-y-8 mb-12">
               {heaps.map((count, heapIndex) => (
                 <div 
                    key={heapIndex} 
                    className="flex items-center space-x-2 p-4 bg-gray-50 rounded-xl min-h-[80px]"
                 >
                    <span className="text-gray-400 font-mono mr-4 w-6">{count}</span>
                    <div className="flex flex-wrap gap-2">
                       {Array.from({ length: count }).map((_, objectIndex) => (
                          <button
                             key={objectIndex}
                             onMouseEnter={() => {
                                 if (!isBotThinking && !gameOver) {
                                     setHoveredHeap(heapIndex);
                                     setHoveredIndex(objectIndex);
                                 }
                             }}
                             onMouseLeave={() => {
                                 setHoveredHeap(null);
                                 setHoveredIndex(null);
                             }}
                             onClick={() => handleObjectClick(heapIndex, objectIndex)}
                             disabled={gameOver || isBotThinking}
                             className={`
                                w-10 h-10 rounded-full border-2 transition-all duration-200 shadow-sm
                                ${
                                   isObjectHovered(heapIndex, objectIndex)
                                   ? "bg-red-400 border-red-500 transform scale-110 opacity-60" // Highlight for removal
                                   : "bg-blue-400 border-blue-600 hover:bg-blue-500"
                                }
                                ${gameOver && "opacity-50 cursor-not-allowed"}
                             `}
                          />
                       ))}
                       {count === 0 && <span className="text-gray-300 italic text-sm self-center">Empty Heap</span>}
                    </div>
                 </div>
               ))}
            </div>
            
            <div className="text-sm text-gray-600 text-center">
              {!gameOver && (
                <p>
                   Hover over an object to select it and all objects to its right. Click to remove them.
                </p>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default NimGame;
